import React, { useState, useEffect, useCallback, useRef } from "react";
import "./Profile.scss";
import { Link, useParams } from "react-router-dom";
import { Stack } from "@mui/system";
import InstagramIcon from "@mui/icons-material/Instagram";
import { FollowButton } from "./FollowButton";
import { ShareProfileButton } from "./ShareProfileButton";
import LoadingView from "../components/elements/LoadingView";
import { getUserById } from "../services/user";

import {
  getTrip,
  getUserTrips,
  getUserTrip,
  isUserALocalPartnerOrLeader,
} from "../services/trip";
import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import ReactPlayer from "react-player";
import { getUserReviews, getUserExtraMedia } from "../services/user";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react/dist/iconify.js";
import { capitalize, useTheme } from "@mui/material";
import useQueryParams from "../customHooks/useQueryParams";
import dayjs from "dayjs";
import { ImageResizable } from "../components/elements/ImageResizeable";
import { useGlobalState } from "../GlobalStates";
import { CopyToClipboard } from "react-copy-to-clipboard";

import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FlutterDashIcon from "@mui/icons-material/FlutterDash";
import authAxios from "../authAxios";
import { useTabletOrMobile } from "../hooks/responsive";
import { useTranslation } from "react-i18next";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import { formatCurrency } from "../utils/currency";
import { getReferralAmount } from "../utils/multi-language";
import { useUserLocale } from "../customHooks/useUserLocale";

export const UserProfile = ({ passing_user_id = null }) => {
  const { language, community, currency, loading, handleChangeLocale } =
    useUserLocale();
  const { t } = useTranslation();

  const { palette } = useTheme();
  const [_, setOpenAlertBannerMsg] = useGlobalState("openAlertBannerMsg");

  const [openDuckLevelsDialog, setOpenDuckLevelsDialog] = useState(false);

  const { user_id } = useParams();
  const trip_id = useQueryParams().get("trip_id");
  const [user] = useGlobalState("user");

  const [profile, setProfile] = useState(null);
  const [userPastTrips, setUserPastTrips] = useState([]);
  const [userUpcomingTrips, setUserUpcomingTrips] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [extraMedia, setExtraMedia] = useState([]);
  const [
    { is_trip_leader, is_local_partner, local_partner_country },
    setIsUserALocalPartnerOrLeader,
  ] = useState({ is_trip_leader: false, is_local_partner: false });

  const isMobile = useTabletOrMobile();

  const userId = passing_user_id ? passing_user_id : user_id;

  const isUserProfile = userId === user?.id;

  const isInApp = window.isInApp;

  const openInstagramProfile = (instagramUrl) => {
    // Extract username from Instagram URL
    const urlParts = instagramUrl.split("/");
    const username =
      urlParts[urlParts.length - 1] || urlParts[urlParts.length - 2];
    const appLink = `instagram://user?username=${username}`;
    const webLink = `https://instagram.com/${username}`;

    // Try opening the Instagram app first
    window.location.href = appLink;

    // Fallback to web version after a delay if the app is not installed
    setTimeout(() => {
      window.location.href = webLink;
    }, 500); // Adjust the delay as needed
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profileRes = await getUserById(userId);
        setProfile(profileRes.data);
      } catch (error) {
        console.error(error);
      }
    };

    const getIsUserALocalPartnerOrLeader = async () => {
      const { is_trip_leader, is_local_partner, local_partner_country } = (
        await isUserALocalPartnerOrLeader(userId, trip_id)
      ).data;
      setIsUserALocalPartnerOrLeader({
        is_trip_leader,
        is_local_partner,
        local_partner_country,
      });
    };

    const getUserPastSotravelTrips = async () => {
      const res = await getUserTrips(userId);
      setUserUpcomingTrips(res.data.upcomingTrips);
      setUserPastTrips(res.data.pastTrips);
    };

    const getReviews = async () => {
      try {
        const res = await getUserReviews(userId);
        const mediaRes = await getUserExtraMedia(userId);
        setReviews(res.data);
        setExtraMedia(mediaRes.data);
      } catch (err) {
        console.log("failed to get user reviews", err);
      }
    };

    fetchProfile();
    getUserPastSotravelTrips();
    getReviews();

    if (trip_id) {
      getIsUserALocalPartnerOrLeader();
    }
  }, [trip_id, userId]);

  const DuckLevelsDialog = () => {
    return (
      <Dialog
        open={openDuckLevelsDialog}
        onClose={() => setOpenDuckLevelsDialog(false)}
        aria-labelledby="duck-levels-dialog-title"
        fullWidth={isMobile}
        maxWidth="sm"
      >
        <div style={{ padding: "1.5rem" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h2
              id="duck-levels-dialog-title"
              style={{ margin: 0, fontWeight: "bold", fontSize: "1.5rem" }}
            >
              {t("duck_levels", { defaultValue: "Duck Levels" })}
            </h2>
            <button
              onClick={() => setOpenDuckLevelsDialog(false)}
              style={{ background: "none", border: "none", cursor: "pointer" }}
            >
              <CloseIcon />
            </button>
          </div>

          <Stack spacing={2}>
            <div
              className="duck-level-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>{t("duckling", { defaultValue: "Duckling" })}</strong>
                <p>
                  {t("community_member", { defaultValue: "Community member" })}
                </p>
              </div>
              <FlutterDashIcon style={{ color: "#9E9E9E", fontSize: "28px" }} />
            </div>

            <div
              className="duck-level-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>
                  {t("junior_duck", { defaultValue: "Junior Duck" })}
                </strong>
                <p>
                  {t("we_all_start_somewhere", {
                    defaultValue: "We all start somewhere",
                  })}
                </p>
              </div>
              <FlutterDashIcon style={{ color: "#4FC3F7", fontSize: "28px" }} />
            </div>

            <div
              className="duck-level-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>
                  {t("senior_duck", { defaultValue: "Senior Duck" })}
                </strong>
                <p>
                  {t("experienced_group_leader", {
                    defaultValue: "Experienced group leader",
                  })}
                </p>
              </div>
              <FlutterDashIcon style={{ color: "#2196F3", fontSize: "28px" }} />
            </div>

            <div
              className="duck-level-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>
                  {t("super_duck", { defaultValue: "Super Duck" })}
                </strong>
                <p>
                  {t("group_leader_high_aura", {
                    defaultValue: "Group leader with high aura",
                  })}
                </p>
              </div>
              <FlutterDashIcon style={{ color: "#FFC107", fontSize: "28px" }} />
            </div>

            <div
              className="duck-level-item"
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ flex: 1 }}>
                <strong>
                  {t("skilled_duck", { defaultValue: "Skilled Duck" })}
                </strong>
                <p>
                  {t("group_leader_maximum_aura", {
                    defaultValue: "Group leader with maximum aura",
                  })}
                </p>
              </div>
              <FlutterDashIcon style={{ color: "#FF9800", fontSize: "28px" }} />
            </div>
          </Stack>
        </div>
      </Dialog>
    );
  };

  const scrollValue = 298;
  const carouselReviewRef = useRef(null);
  const scrollReviewLeft = () => {
    carouselReviewRef.current.scrollLeft -= scrollValue;
  };
  const scrollReviewRight = () => {
    carouselReviewRef.current.scrollLeft += scrollValue;
  };
  const carouselMediaRef = useRef(null);
  const scrollMediaLeft = () => {
    carouselMediaRef.current.scrollLeft -= scrollValue;
  };
  const scrollMediaRight = () => {
    carouselMediaRef.current.scrollLeft += scrollValue;
  };

  const displayExtraMedia = (url, type) => {
    if (type === "image") {
      return (
        <img
          src={url}
          style={{
            height: "100%",
            width: "100%",
            objectFit: "cover",
            borderRadius: "10px",
          }}
        />
      );
    } else if (type === "video") {
      return (
        <ReactPlayer
          url={url}
          controls
          playing={false}
          width="100%"
          height="100%"
        />
      );
    }
  };
  const [reviewMoreIndex, setReviewMoreIndex] = useState(-1);
  const reviewMaxLength = 120;
  const shortenText = (text) => {
    if (text.length === 0) {
      const positiveWords = [
        t("awesome", { defaultValue: "Awesome!" }),
        t("great", { defaultValue: "Great!" }),
        t("amazing", { defaultValue: "Amazing!" }),
        t("fantastic", { defaultValue: "Fantastic!" }),
      ];
      return positiveWords[Math.floor(Math.random() * positiveWords.length)];
    }
    if (text.length > reviewMaxLength) {
      return `"${text.slice(0, reviewMaxLength)} ...`;
    }
    return `"${text}"`;
  };

  const handleAppNavigation = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("connectionNavigationTo");
    } else {
      console.error("ReactNativeWebView bridge is not available");
    }
  };

  if (!profile) {
    return <LoadingView />;
  }

  return (
    <div className="ProfileWrapper">
      <div className="ProfileContainer">
        <div className="ProfileImageAndDescContainer">
          <div className="ProfileImageContainer">
            <img src={profile.image} alt={profile.first_name} />
            {(is_trip_leader || is_local_partner) && (
              <div
                style={{
                  display: "flex",
                  position: "absolute",
                  bottom: "0.1rem",
                  right: "0.1rem",
                  height: "2.5rem",
                  width: "2.5rem",
                  borderRadius: "50%",
                  backgroundColor: palette.primary.main,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {is_local_partner ? (
                  <Icon
                    icon="ic:round-verified-user"
                    color="white"
                    fontSize={"1.8rem"}
                  />
                ) : (
                  <Icon icon="mdi:crown" color="white" fontSize={"1.8rem"} />
                )}
              </div>
            )}
          </div>

          <Stack className="ProfileSocialsContainer">
            {isInApp ? (
              isUserProfile ? (
                <button
                  className="ProfileFollowContainer"
                  onClick={() => {
                    handleAppNavigation();
                  }}
                >
                  <span className="ProfileFollowNumber">
                    {user.following_count}
                  </span>
                  <span className="ProfileFollowText">
                    {t("following", { defaultValue: "Following" })}
                  </span>
                </button>
              ) : (
                <button
                  className="ProfileFollowContainer"
                  onClick={() => {
                    handleAppNavigation();
                  }}
                >
                  <span className="ProfileFollowNumber">
                    {profile.following_count}
                  </span>
                  <span className="ProfileFollowText">
                    {t("following", { defaultValue: "Following" })}
                  </span>
                </button>
              )
            ) : (
              <Link
                to={
                  isUserProfile
                    ? `/profile/connections`
                    : `/profile/${user_id}/connections`
                }
              >
                <button className="ProfileFollowContainer">
                  <span className="ProfileFollowNumber">
                    {isUserProfile
                      ? user.following_count
                      : profile.following_count}
                  </span>
                  <span className="ProfileFollowText">
                    {t("following", { defaultValue: "Following" })}
                  </span>
                </button>
              </Link>
            )}

            {isInApp ? (
              isUserProfile ? (
                <button
                  className="ProfileFollowContainer"
                  onClick={() => {
                    handleAppNavigation();
                  }}
                >
                  <span className="ProfileFollowNumber">
                    {user.follower_count}
                  </span>
                  <span className="ProfileFollowText">
                    {t("followers", { defaultValue: "Followers" })}
                  </span>
                </button>
              ) : (
                <button
                  className="ProfileFollowContainer"
                  onClick={() => {
                    handleAppNavigation();
                  }}
                >
                  <span className="ProfileFollowNumber">
                    {profile.follower_count}
                  </span>
                  <span className="ProfileFollowText">
                    {t("followers", { defaultValue: "Followers" })}
                  </span>
                </button>
              )
            ) : (
              <Link
                to={
                  isUserProfile
                    ? `/profile/connections`
                    : `/profile/${user_id}/connections`
                }
              >
                <button className="ProfileFollowContainer">
                  <span className="ProfileFollowNumber">
                    {isUserProfile
                      ? user.follower_count
                      : profile.follower_count}
                  </span>
                  <span className="ProfileFollowText">
                    {t("followers", { defaultValue: "Followers" })}
                  </span>
                </button>
              </Link>
            )}

            <button className="ProfileFollowContainer">
              <span className="ProfileFollowNumber">{profile.credits}</span>
              <span className="ProfileFollowText">
                {t("socredits", { defaultValue: "Socredits" })}
              </span>
            </button>
          </Stack>

          {isUserProfile ? (
            <Stack className="ProfileSocialsContainer">
              {isInApp ? (
                <button
                  className="ProfileEditButton"
                  onClick={() => {
                    if (window.ReactNativeWebView) {
                      window.ReactNativeWebView.postMessage(
                        "navigateToEditProfile"
                      );
                    } else {
                      console.error(
                        "ReactNativeWebView bridge is not available"
                      );
                    }
                  }}
                >
                  {t("edit_profile", { defaultValue: "Edit profile" })}
                </button>
              ) : (
                <Link to="/trip-starter-form">
                  <button className="ProfileEditButton">
                    {t("edit_profile", { defaultValue: "Edit profile" })}
                  </button>
                </Link>
              )}{" "}
              <CopyToClipboard
                text={`Use this link to get ${getReferralAmount(
                  currency,
                  "welcome"
                )} off your first Sotravel trip! 👉 www.sotravel.com/welcome-discount?code=${
                  user.id
                }`}
              >
                <button
                  className="ProfileEditButton"
                  style={{
                    backgroundColor: "deepskyblue",
                    color: "white",
                  }}
                  onClick={() => {
                    if (user.influencer_type === "Popular duck") {
                      window.location.href =
                        "https://www.sotravel.com/affiliate";
                    } else {
                      setOpenAlertBannerMsg(
                        t("referral_link_copied", {
                          defaultValue: "Referral link copied",
                        })
                      );
                    }
                  }}
                >
                  {t("refer_and_earn", { defaultValue: "Refer & earn" })}
                </button>
              </CopyToClipboard>
            </Stack>
          ) : (
            <Stack
              direction="row"
              spacing={1}
              style={{ marginTop: "12px", marginBottom: "8px" }}
            >
              <FollowButton profile={profile} />
              <ShareProfileButton userId={userId} />
            </Stack>
          )}

          <Stack className="ProfileSocialsContainer">
            {profile.socials_instagram && (
              <button
                className="ProfileSocialsButton"
                onClick={() => openInstagramProfile(profile.socials_instagram)}
              >
                <InstagramIcon
                  style={{
                    fontSize: "17px",
                    marginRight: "4px",
                    color: "deepskyblue",
                  }}
                />
                <span>Instagram</span>
              </button>
            )}
          </Stack>

          {is_trip_leader && (
            <div style={{ width: "100%" }}>
              <p className="ProfileHeader">
                {t("what_is_trip_leader", {
                  defaultValue: "What is a trip leader?",
                })}
              </p>

              <div>
                <div className="ProfileDescription">
                  <span>
                    {t("trip_leader_description", {
                      defaultValue:
                        "A trip leader is a participant that we have identified to help make the trip a success! Do help them out by supporting them!",
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}

          {is_local_partner && local_partner_country ? (
            <p
              className="ProfileHeader"
              style={{ marginBottom: "0.5rem", marginTop: "0.5rem" }}
            >
              {t("word_from_team", {
                defaultValue: `A word from our ${local_partner_country} Team`,
                local_partner_country: local_partner_country,
              })}
            </p>
          ) : (
            <p
              className="ProfileHeader"
              style={{ marginBottom: "0.5rem", marginTop: "0.5rem" }}
            >
              {t("about_person", {
                defaultValue: `About ${profile.first_name}`,
                first_name: profile.first_name,
              })}
            </p>
          )}

          <div className="ProfileDescription">{profile.description}</div>

          <Stack className="ProfileExtraInfoContainer" spacing={0.5}>
            {profile.host_type && (
              <div className="ProfileExtraInfoRow">
                <FlutterDashIcon style={{ fontSize: "18px" }} />{" "}
                <span
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => {
                    setOpenDuckLevelsDialog(true);
                  }}
                >
                  {t("duck_type", {
                    defaultValue: `Duck type: ${profile.host_type}`,
                    host_type: profile.influencer_type
                      ? profile.influencer_type
                      : profile.host_type,
                  })}
                </span>
              </div>
            )}

            {profile.obsessed_with && (
              <div className="ProfileExtraInfoRow">
                <FavoriteBorderOutlinedIcon style={{ fontSize: "18px" }} />
                <span>
                  {t("obsessed_with")}: {profile.obsessed_with}
                </span>
              </div>
            )}

            {profile.certifications && (
              <div className="ProfileExtraInfoRow">
                <ArticleOutlinedIcon style={{ fontSize: "18px" }} />{" "}
                <span>
                  {t("certifications")}: {profile.certifications}
                </span>
              </div>
            )}

            {profile.school_and_subject && (
              <div className="ProfileExtraInfoRow">
                <SchoolOutlinedIcon style={{ fontSize: "18px" }} />{" "}
                <span>
                  {t("school")}: {profile.school_and_subject}
                </span>
              </div>
            )}

            {profile.work_and_role && (
              <div className="ProfileExtraInfoRow">
                <WorkOutlineOutlinedIcon style={{ fontSize: "18px" }} />{" "}
                <span>
                  {t("work")}: {profile.work_and_role}
                </span>
              </div>
            )}
          </Stack>

          {reviews.length > 0 && (
            <div className="TripDetailsArrowHeaderContainer">
              <span className="TripDetailsArrowHeader">
                {t("person_reviews", {
                  defaultValue: `${profile.first_name}'s reviews`,
                  first_name: profile.first_name,
                })}
              </span>
              <Stack
                className="TripDetailslArrowContainer"
                flexDirection="row"
                justifyContent="space-between"
              >
                <button onClick={scrollReviewLeft}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow left" />
                </button>

                <button onClick={scrollReviewRight}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow right" />
                </button>
              </Stack>
            </div>
          )}
          {reviews.length > 0 && (
            <Stack
              className="TripDetailsCarouselItemContainer"
              ref={carouselReviewRef}
            >
              {reviews.map((review, index) => {
                return (
                  <div
                    className="TripDetailsCarouselItemBox"
                    style={
                      reviewMoreIndex === index
                        ? {
                            minWidth: review.positive.length * 2.1,
                          }
                        : {}
                    }
                  >
                    <span>
                      {reviewMoreIndex === index
                        ? `"${review.positive}"`
                        : shortenText(review.positive)}{" "}
                      {review.positive.length > reviewMaxLength &&
                        reviewMoreIndex !== index && (
                          <span
                            style={{ fontSize: "15px", fontWeight: "bold" }}
                            onClick={() => setReviewMoreIndex(index)}
                          >
                            {t("more", { defaultValue: "more" })}
                          </span>
                        )}
                    </span>
                    <Stack
                      flexDirection="row"
                      alignItems="center"
                      className="TripDetailsReviewImgWrapper"
                    >
                      <div className="TripDetailsReviewImgContainer">
                        <img src={review.review_by_image} />
                      </div>
                      <span>{review.review_by_first_name}</span>
                    </Stack>
                  </div>
                );
              })}
            </Stack>
          )}

          {userUpcomingTrips.length > 0 && (
            <>
              <p className="ProfileHeader" style={{ marginTop: "1.5rem" }}>
                {t("upcoming_trips", { defaultValue: "Upcoming trips" })}
              </p>
              <div className="ProfileDescription">
                {userUpcomingTrips.map((trip, index) => (
                  <TripCard trip={trip} index={index} />
                ))}
              </div>
            </>
          )}

          {userPastTrips.length > 0 && (
            <>
              <p className="ProfileHeader">
                {t("previously_attended", {
                  defaultValue: "Previously attended",
                })}
              </p>
              <div className="ProfileDescription">
                {userPastTrips.map((trip, index) => (
                  <TripCard trip={trip} index={index} />
                ))}
              </div>
            </>
          )}

          {extraMedia.length > 0 && (
            <div className="TripDetailsArrowHeaderContainer">
              <span className="TripDetailsArrowHeader">
                {t("person_in_action", {
                  defaultValue: `${profile.first_name} in action`,
                  first_name: profile.first_name,
                })}
              </span>
              <Stack
                className="TripDetailslArrowContainer"
                flexDirection="row"
                justifyContent="space-between"
              >
                <button onClick={scrollMediaLeft}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow left" />
                </button>

                <button onClick={scrollMediaRight}>
                  <ExpandCircleDownOutlinedIcon className="TripDetailsArrow right" />
                </button>
              </Stack>
            </div>
          )}

          {extraMedia.length > 0 && (
            <Stack
              className="TripDetailsCarouselItemContainer"
              ref={carouselMediaRef}
            >
              {extraMedia.map((media, index) => (
                <div className="TripDetailsCarouselMediaBox">
                  <div className="TripDetailsCarouselVideoBox">
                    {displayExtraMedia(media.media_url, media.media_type)}
                  </div>
                </div>
              ))}
            </Stack>
          )}

          <div style={{ width: "100%", height: "3rem" }}></div>
        </div>
      </div>

      <DuckLevelsDialog />
    </div>
  );
};

export default UserProfile;

function TripCard({ trip, index }) {
  const [{ projects: tProjects }] = useGlobalState("translations");

  return (
    <Link
      style={{ flex: "none", width: "140px" }}
      to={`/trips/${trip.project_id}`}
      key={trip.id}
      target="_blank"
    >
      {trip.main_images.length && (
        <ImageResizable
          src={trip.main_images[0]}
          alt={`trip-${trip.name}-${index}`}
          style={{
            width: "140px",
            height: "140px",
            objectFit: "cover",
            borderRadius: "10px",
            marginBottom: "0.5rem",
          }}
          size="l"
        />
      )}
      <div style={{ fontWeight: 600 }}>
        {tProjects[trip.project_id]?.project_name || trip.name}
      </div>
      <div>{dayjs(trip.start_date).format("DD MMM YY")}</div>
    </Link>
  );
}
